import BigNumber from 'bignumber.js';
import { defineStore } from 'pinia';
import { BLOCKED_CURRENCIES } from '~/utils/constants';
import { useStoreRoot } from '../abstract/root';
import { useStoreConverterCurrency } from '../client/converterCurrency';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import { softswiss } from '../transport/softswiss';
import { useStoreSsPlayer } from './player';
import type { ISsCentrifugeBalanceChannel } from '~/types/softswiss/ssCentrifuge';
import type { ISsPlayerBalance } from '~/types/softswiss/ssPlayerBalance';
import type { IRequestOptions } from '~/types/transport/requestOptions';

export const useStoreSsAccounts = defineStore('ssAccounts', {
  state: () => ({
    balance: [] as ISsPlayerBalance[],
    allBalances: [] as ISsPlayerBalance[],
  }),
  getters: {
    /**
     * Returns user balances at correct format
     * (500 amount_cents --> 5 USD)
     * @param state store state
     * @returns array of balances with formatted amounts
     */
    balanceFormatted(state): ISsPlayerBalance[] {
      const storeCurrencyMenu = useStoreCurrencyMenu();

      return state.balance.map((b) => {
        const precision =
          storeCurrencyMenu.currencies.find((c) => c.currencyCode === b.currency)?.precision || 0;
        if (precision >= 0) {
          b.amount_formatted = new BigNumber(b.amount_cents || 0).shiftedBy(-precision).toNumber();
          b.amount_formatted_string = new BigNumber(b.amount_cents || 0)
            .shiftedBy(-precision)
            .toFormat();
        }

        const balanceWithAdditionalFields = this.allBalances.find(
          (item) => b.currency === item.currency
        );

        b.withdrawable = new BigNumber(balanceWithAdditionalFields?.available_to_cashout_cents || 0)
          .shiftedBy(-precision)
          .toFormat();
        b.lockedByBonus = new BigNumber(
          b.amount_cents - (balanceWithAdditionalFields?.available_to_cashout_cents || 0)
        )
          .shiftedBy(-precision)
          .toFormat();
        return b;
      });
    },

    /**
     * Returns balances with USD equivalent for crypto currencies
     *
     * @returns array of balances with dollar amount
     */
    balanceWithDollarEquivalent(): ISsPlayerBalance[] {
      const storeConverterCurrency = useStoreConverterCurrency();

      return this.balanceFormatted.map((b) => {
        if (storeConverterCurrency.rates[b.currency] > 0 && b.amount_formatted) {
          b.amount_dollar_equivalent = new BigNumber(b.amount_formatted)
            .multipliedBy(storeConverterCurrency.rates[b.currency])
            .decimalPlaces(2)
            .toNumber();
        }
        return b;
      });
    },

    activeBalance(): ISsPlayerBalance | null {
      const storeRoot = useStoreRoot();

      const userCurrency =
        this.balance.find((el: ISsPlayerBalance) => el.currency === storeRoot.userCurrency) ||
        (this.balance && this.balance[0]);

      return userCurrency || null;
    },

    activeBalanceWithDollarEquivalent(): ISsPlayerBalance | null {
      const storeRoot = useStoreRoot();

      return (
        this.balanceWithDollarEquivalent.find((el) => el.currency === storeRoot.userCurrency) ||
        null
      );
    },

    /**
     * get list of currency codes which exist in player account (only if authorised)
     *
     * @returns list of currency codes which exist in player account
     */
    accountsList(): string[] {
      return this.balance.map((b) => b.currency);
    },
  },
  actions: {
    /**
     * Getting player balance from SS API
     * @param context - context
     * @param options - optional param for set options of request
     */
    async loadPlayerBalance(options?: IRequestOptions): Promise<void> {
      const storeSsPlayer = useStoreSsPlayer();

      try {
        if (!storeSsPlayer.isPlayerAuthorised) {
          return;
        }

        const playerBalance: ISsPlayerBalance[] | null = await softswiss().get({
          path: '/api/player/accounts',
          updateInterval: 1000 * 60, // 1 minute
          ...options,
        });

        if (playerBalance && playerBalance.length > 0) {
          this.balance = playerBalance.filter(
            (item) => !BLOCKED_CURRENCIES.includes(item.currency)
          );
          if (BLOCKED_CURRENCIES.includes(storeSsPlayer.playerCurrency || '')) {
            await useStoreRoot().setUserCurrency('USD');
          }
        }
      } catch (e: any) {
        console.error(e);
      }
    },

    async loadAllPlayerBalance(options?: IRequestOptions): Promise<void> {
      const storeSsPlayer = useStoreSsPlayer();

      try {
        if (!storeSsPlayer.isPlayerAuthorised) {
          return;
        }

        const playerBalance: ISsPlayerBalance[] | null = await softswiss().get({
          path: '/api/player/accounts?compatibility=false',
          updateInterval: 1000 * 60, // 1 minute
          ...options,
        });

        if (playerBalance && playerBalance.length > 0) {
          this.allBalances = playerBalance;
        }
      } catch (e: any) {
        console.error(e);
      }
    },
    updatePlayerBalance(balance: ISsCentrifugeBalanceChannel) {
      const index = this.balance.findIndex((item) => item.currency === balance.currency);
      if (index !== -1) {
        this.balance.splice(index, 1, { ...this.balance[index], ...balance });
      }
    },
  },
});
